import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import { errText } from "@/v2/lib/hooks";
import "@/v2/v2.css";
import { Eye, EyeOff, ShieldCheck, UserPlus } from "lucide-react";
import storyPhoto from "@/v2/assets/official/nabawi_gate.jpg";

export default function V2Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`أهلًا بك، ${u.name}`);
      if (u.needs_onboarding) navigate("/onboarding", { replace: true });
      else if (u.is_approved === false) navigate("/pending", { replace: true });
      else navigate((u.role === "employee") ? "/masar" : "/admin", { replace: true });
    } catch (err) {
      setError(errText(err, "تعذّر تسجيل الدخول، تحقق من البيانات."));
    } finally { setLoading(false); }
  };

  return (
    <div className="v2-root brand-auth min-h-[100dvh] grid lg:grid-cols-2" dir="rtl">
      {/* visual side */}
      <div className="brand-auth-story theme-navy relative hidden lg:flex flex-col justify-end gap-8 p-12 overflow-hidden">
        <img src={storyPhoto} alt="" aria-hidden="true" className="absolute inset-x-0 top-0 w-full h-[58%] object-cover" />
        <div className="absolute z-10 top-12 right-12"><BrandLockup tone="dark" /></div>
        <div className="relative z-10">
          <p className="font-display text-[2.25rem] leading-[1.35] mb-3 text-ink">رحلة تميّز رقمية<br />تصنع الخبراء</p>
          <p className="text-ink leading-relaxed max-w-md text-lg opacity-90">عشرون يومًا من التحديات التفاعلية، تقييم ذكي بالذكاء الاصطناعي، ونظام مستويات ونقاط يحفّزك للتقدّم كل يوم.</p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-ink-2 text-sm"><ShieldCheck size={16} className="text-ok" /> بيئة آمنة — منصة داخلية للموظفين</div>
      </div>

      {/* form side */}
      <div className="brand-auth-main flex items-center justify-center p-6 sm:p-12">
        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          onSubmit={submit} data-testid="v2-login-form" className="w-full max-w-md">
          <div className="lg:hidden mb-8"><BrandLockup tone="light" /></div>
          <h1 className="mb-2">تسجيل الدخول</h1>
          <p className="text-ink-2 mb-8">مرحبًا بعودتك، تابع رحلتك نحو التميّز.</p>

          {error && <div data-testid="v2-login-error" className="mb-4 rounded-2xl bg-danger/15 border border-danger/40 text-danger px-4 py-3 text-sm">{error}</div>}

          <label className="block text-sm font-semibold mb-2 text-ink-2">الاسم أو البريد الإلكتروني</label>
          <input data-testid="v2-login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="text"
            placeholder="اكتب اسمك كما سجّلته، أو بريدك الإلكتروني"
            className="w-full h-13 rounded-2xl bg-surface-2 border border-line px-4 py-3.5 mb-5 outline-none focus:border-beige/50 focus:ring-2 focus:ring-ok/30 text-ink placeholder:text-ink-2/60" />

          <label className="block text-sm font-semibold mb-2 text-ink-2">كلمة المرور</label>
          <div className="relative mb-4">
            <input data-testid="v2-login-password" value={password} onChange={(e) => setPassword(e.target.value)} type={show ? "text" : "password"}
              className="w-full rounded-2xl bg-surface-2 border border-line px-4 py-3.5 pe-12 outline-none focus:border-beige/50 focus:ring-2 focus:ring-ok/30 text-ink" />
            <button type="button" onClick={() => setShow(!show)} className="absolute inset-y-0 left-3 flex items-center text-ink-2">{show ? <EyeOff size={20} /> : <Eye size={20} />}</button>
          </div>

          <div className="flex items-center justify-between mb-6 text-sm">
            <label className="flex items-center gap-2 text-ink-2 cursor-pointer">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-[#006654]" /> تذكرني
            </label>
            <button type="button" onClick={() => toast.info("يرجى التواصل مع إدارة المنصة لإعادة تعيين كلمة المرور.")} className="v2-gold-text font-bold">نسيت كلمة المرور؟</button>
          </div>

          <button data-testid="v2-login-submit" type="submit" disabled={loading} className="v2-btn w-full">
            {loading ? "جارٍ الدخول..." : "دخول إلى المنصة"}
          </button>

          <div className="mt-6 text-center text-sm text-ink-3">ليس لديك حساب؟</div>
          <Link to="/register" data-testid="v2-login-to-register"
            className="mt-5 v2-btn-ghost w-full flex items-center justify-center gap-2">
            <UserPlus size={17} /> إنشاء حساب جديد
          </Link>
        </motion.form>
      </div>
    </div>
  );
}
